import React, { PropsWithChildren, useCallback, useState } from "react";
import { KeyboardStickyView } from "react-native-keyboard-controller";
import { LayoutChangeEvent, Platform } from "react-native";
import { Portal } from "@gorhom/portal";

type OnLayoutType = (event: LayoutChangeEvent) => void;

export type KeyboardStickyComponentParams = {
    isVisible?: boolean;
    //
    component?: React.ReactNode;
    onLayout?: OnLayoutType;
    //
    containerComponent?: React.ReactNode;
    containerProps?: unknown;
};

interface KeyboardStickyComponentContextInterface {
    setKeyboardStickyComponent: (params: KeyboardStickyComponentParams) => void;
    hasStickyComponent: boolean;
}

export const KeyboardStickyComponentManagerContext =
    React.createContext<KeyboardStickyComponentContextInterface>({
        setKeyboardStickyComponent: () => {},
        hasStickyComponent: false,
    });

export const KEYBOARD_STICKY_COMPONENT_ANIMATION_DELAY = 500;

// Name of the PortalHost mounted by SelectChooser inside its iOS FullWindowOverlay container.
export const KEYBOARD_STICKY_COMPONENT_HOST_NAME = "KeyboardStickyComponent";

/*
 ** This component manages a keyboard sticky component that appears above the keyboard.
 */

const KeyboardStickyComponentManager = ({ children }: PropsWithChildren) => {
    const [stickyComponent, setStickyComponent] = useState<React.ReactNode>();
    const [onLayoutEvent, setOnLayoutEvent] = useState<OnLayoutType>();

    const _setKeyboardStickyComponent = useCallback((params: KeyboardStickyComponentParams) => {
        if (params.isVisible) {
            setStickyComponent(params.component);
            setOnLayoutEvent(() => params.onLayout);
        } else {
            setTimeout(() => {
                setStickyComponent(undefined);
            }, KEYBOARD_STICKY_COMPONENT_ANIMATION_DELAY); // Approximative time for closing keyboard
        }
    }, []);

    // We need to keep this "wrapper" function, as it seems that KeyboardStickyView's `onLayout` will not be fired for updates if it was undefined when mounting
    const _onLayoutEvent = useCallback(
        (event: LayoutChangeEvent) => {
            onLayoutEvent?.(event);
        },
        [onLayoutEvent],
    );

    const stickyView = (
        <KeyboardStickyView
            enabled={!!stickyComponent}
            onLayout={_onLayoutEvent}
            style={[
                {
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    zIndex: 10000,
                },
            ]}
        >
            {stickyComponent}
        </KeyboardStickyView>
    );

    return (
        <KeyboardStickyComponentManagerContext.Provider
            value={{
                setKeyboardStickyComponent: _setKeyboardStickyComponent,
                hasStickyComponent: !!stickyComponent,
            }}
        >
            {children}
            {/* On iOS the SelectChooser sheet lives in a FullWindowOverlay attached directly to
                the UIWindow, above native modals AND above anything mounted in the root React
                tree — including this manager's KeyboardStickyView (BAS-2851). Teleport the sticky
                view into a PortalHost that SelectChooser mounts AFTER the sheet inside that same
                overlay, so it paints above both (window subview order = mount order). On
                Android/web root overlays already paint above the sheet via zIndex. */}
            {Platform.OS === "ios" ? (
                <Portal hostName={KEYBOARD_STICKY_COMPONENT_HOST_NAME}>{stickyView}</Portal>
            ) : (
                stickyView
            )}
        </KeyboardStickyComponentManagerContext.Provider>
    );
};

export default KeyboardStickyComponentManager;
